import { useQuery } from 'react-query'

import { fetchInvoicesSummary, fetchSalesDocuments } from '@features/invoices/query-api'
import { InvoicesSummaryData } from '@features/invoices/types/invoicesSummaryData'
import { FetchSalesDocumentsParams } from '@features/invoices/types/salesDocument/fetchSalesDocumentsParams'
import { fetchInvoices, InvoicesQueryParams } from '@modules-deprecated/app/invoices/query-api'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { QueryKeys } from '../../../../../enums/queryKeys'
import { usePeriodDates } from '../../../../../hooks/usePeriodDates'
import { useFiscalYearQueryParams } from '../../../hooks/useFiscalYearQueryParams'
import { fetchSummary, InvoicesSummary, StatsDataQueryParams } from '../../../services/query-api'

const INVOICES_MINIMAL_PAGE_SIZE = 1
const EXTERNAL_INVOICES_MINIMAL_PAGE_SIZE = 25

const invoicesQueryParams: InvoicesQueryParams = {
  state: 'approved',
  pageSize: INVOICES_MINIMAL_PAGE_SIZE,
  offset: 0,
}

const externalInvoicesQueryParams: FetchSalesDocumentsParams<'invoices'> = {
  documentsListType: 'invoices',
  pageSize: EXTERNAL_INVOICES_MINIMAL_PAGE_SIZE,
}

interface UseInvoiceWidgetDataResponse {
  summary?: InvoicesSummary
  externalInvoicesSummary?: InvoicesSummaryData
  isLoading: boolean
  shouldShowEmptyState: boolean
  doInvoicesExist?: boolean
}

export const useInvoiceWidgetData = (): UseInvoiceWidgetDataResponse => {
  const { organization } = useUserOrganization()
  const { queryParams: fiscalYearQueryParams } = useFiscalYearQueryParams(organization?.id)
  const isExternalInvoicesSource = organization?.source !== 'billy'
  const { isLoading: isLoadingPeriodDates, fromDate, toDate } = usePeriodDates(fiscalYearQueryParams?.period)

  const { data: summaryData, isLoading: isSummaryDataLoading } = useQuery(
    [QueryKeys.InvoicesSummary, organization?.id, fiscalYearQueryParams],
    () => fetchSummary(organization?.id || '', fiscalYearQueryParams || ({} as StatsDataQueryParams)),
    {
      enabled: !!organization && !isExternalInvoicesSource,
      refetchOnMount: 'always',
    },
  )

  const { data: externalInvoicesSummaryData, isLoading: isExternalInvoicesSummaryDataLoading } = useQuery(
    [QueryKeys.ExternalInvoicesSummary, organization?.id, fiscalYearQueryParams],
    () => fetchInvoicesSummary({ fromDate, toDate }),
    { enabled: !!organization && isExternalInvoicesSource && !isLoadingPeriodDates, refetchOnMount: 'always' },
  )

  const { data: invoicesData, isLoading: isInvoicesDataLoading } = useQuery(
    [QueryKeys.Invoices, organization?.id, invoicesQueryParams],
    () => fetchInvoices(organization?.id || '', invoicesQueryParams),
    {
      enabled: !!organization && !isExternalInvoicesSource,
      refetchOnMount: 'always',
    },
  )

  const { data: externalInvoicesData, isLoading: isExternalInvoicesDataLoading } = useQuery(
    [QueryKeys.InvoicesZervant, organization?.id, externalInvoicesQueryParams],
    () => fetchSalesDocuments(externalInvoicesQueryParams),
    {
      enabled: !!organization && isExternalInvoicesSource,
      refetchOnMount: 'always',
    },
  )

  const isLoading =
    isSummaryDataLoading ||
    isInvoicesDataLoading ||
    !organization ||
    isExternalInvoicesDataLoading ||
    isExternalInvoicesSummaryDataLoading

  const doGeneralInvoicesExist = invoicesData && invoicesData.meta.paging.total > 0
  const doExternalInvoicesExist = externalInvoicesData && externalInvoicesData?.data?.documents.length > 0
  const doInvoicesExist = doGeneralInvoicesExist || doExternalInvoicesExist

  const shouldShowEmptyStateForExternalInvoices =
    !isExternalInvoicesDataLoading && !doExternalInvoicesExist && !!organization
  const shouldShowGeneralEmptyState = !isLoading && !doInvoicesExist && !!organization
  const shouldShowEmptyState = isExternalInvoicesSource
    ? shouldShowEmptyStateForExternalInvoices
    : shouldShowGeneralEmptyState

  return {
    summary: summaryData?.summary,
    externalInvoicesSummary: externalInvoicesSummaryData?.data?.summary,
    isLoading,
    shouldShowEmptyState,
    doInvoicesExist,
  }
}
